import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { httpClient } from "@/shared/api/http-client";
import { getActiveTenantId } from "@/shared/tenant/active-tenant";
import type {
  BulkStatusResult,
  Lead,
  LeadActivity,
  LeadBrowseParams,
  LeadBrowseResponse,
  LeadDetail,
} from "./types";

/** Serialize browse params into a stable, sorted query string (skips empties). */
export function leadsQuery(params: LeadBrowseParams): string {
  const sp = new URLSearchParams();
  const entries = Object.entries(params) as [string, unknown][];
  for (const [k, v] of entries) {
    if (v === undefined || v === null || v === "") continue;
    sp.set(k, String(v));
  }
  sp.sort();
  const s = sp.toString();
  return s ? `?${s}` : "";
}

/**
 * GET /api/leads — server-side search/filter/pagination (ADR-0020).
 * Returns { items, total, page, pageSize, counts }. Tenant is scoped by the API.
 */
export function useBrowseLeads(params: LeadBrowseParams) {
  const qs = leadsQuery(params);
  return useQuery({
    queryKey: ["leads", "browse", getActiveTenantId(), qs] as const,
    queryFn: () => httpClient<LeadBrowseResponse>(`/api/leads${qs}`),
    // Keep the previous page visible while the next loads (no table flash).
    placeholderData: (prev) => prev,
  });
}

/**
 * Lightweight per-campaign totals for the Overview/Analytics KPI strips: one
 * cheap page (pageSize 1) just to read `total` + `counts` by status. Avoids
 * pulling the whole result set client-side (which pagination now caps anyway).
 */
export function useLeadCounts(campaignId: string) {
  const qs = leadsQuery({ campaignId, pageSize: 1 });
  return useQuery({
    queryKey: ["leads", "counts", getActiveTenantId(), campaignId] as const,
    queryFn: () => httpClient<LeadBrowseResponse>(`/api/leads${qs}`),
    enabled: !!campaignId,
  });
}

/** GET /api/leads/:id — full detail for the drawer. Disabled until an id is set. */
export function useLead(id: string | null | undefined) {
  return useQuery({
    queryKey: ["lead", getActiveTenantId(), id] as const,
    queryFn: () => httpClient<LeadDetail>(`/api/leads/${id}`),
    enabled: !!id,
  });
}

/**
 * GET /api/leads/:id/activity — the lead's engagement (Smartlead delivery events per
 * contact) for the lead-profile page. Keyed under the `["lead", …]` prefix so a status
 * change or an enrich refetches it alongside the detail. Disabled until an id is set.
 */
export function useLeadActivity(id: string | null | undefined) {
  return useQuery({
    queryKey: ["lead", "activity", getActiveTenantId(), id] as const,
    queryFn: () => httpClient<LeadActivity>(`/api/leads/${id}/activity`),
    enabled: !!id,
  });
}

/**
 * Approve or reject a single lead. Invalidates every leads/lead query + the
 * campaigns list (whose approved/contacts counts depend on it).
 */
export function useSetLeadStatus() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, action }: { id: string; action: "approve" | "reject" }) =>
      httpClient<Lead>(`/api/leads/${id}/${action}`, { method: "POST" }),
    onSuccess: () => invalidateLeads(qc),
  });
}

/** POST /api/leads/bulk-status — approve/reject a manual selection. */
export function useBulkLeadStatus() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { ids: string[]; status: "APPROVED" | "REJECTED" }) =>
      httpClient<BulkStatusResult>(`/api/leads/bulk-status`, {
        method: "POST",
        body: JSON.stringify(body),
        headers: { "content-type": "application/json" },
      }),
    onSuccess: () => invalidateLeads(qc),
  });
}

function invalidateLeads(qc: ReturnType<typeof useQueryClient>) {
  qc.invalidateQueries({ queryKey: ["leads"] });
  qc.invalidateQueries({ queryKey: ["lead"] });
  qc.invalidateQueries({ queryKey: ["campaigns"] });
}
